/**
 * SYSTEM SETTINGS MODULE — Product Sample Orders Setup. Two tabs:
 *   • Sample Products     — the catalog of products someone can ask for, read
 *                           by the New Sample Request form in the POS &
 *                           Fulfillment module. Ported from the source app's
 *                           admin ProductSamplesSection.
 *   • Request Form Embed  — public sample-request forms, one key per place you
 *                           publish one (backlog item 124). Same panel and same
 *                           table as the POS order-form embed, with kind='sample'
 *                           (migration 0097).
 *
 * Account-scoped reference data via shared/referenceDataService.
 * Workspace owner/admin only (route-gated by RequireWorkspaceAdmin).
 */

import { useEffect, useState } from "react";
import PosEmbedKeysPanel from "./PosEmbedKeysPanel.jsx";
import {
  loadAllProductSamplesAdmin, createProductSample, updateProductSample, deleteProductSample,
} from "../../../shared/utils/referenceDataService.js";

const cell = { padding: "9px 10px", borderBottom: "1px solid var(--border)", verticalAlign: "top" };

export default function ProductSampleOrdersSetupSection() {
  const [tab, setTab] = useState("items");
  return (
    <section className="settings-card wide">
      <h2 style={{ marginTop: 0 }}>Product Sample Orders Setup</h2>
      <p className="muted">
        Manage the products your team can request on the Product Sample request form,
        and publish a public request form on your own site. The POS &amp; Fulfillment
        module reads this list wherever a sample request is submitted.
      </p>
      <div style={{ display: "flex", gap: 8, margin: "4px 0 18px" }}>
        <button type="button" className={tab === "items" ? "" : "secondary"} onClick={() => setTab("items")}>Sample Products</button>
        <button type="button" className={tab === "embed" ? "" : "secondary"} onClick={() => setTab("embed")}>Request Form Embed</button>
      </div>
      {tab === "items" && <SampleProductsPanel />}
      {tab === "embed" && <PosEmbedKeysPanel kind="sample" />}
    </section>
  );
}

// ── Sample Products (the catalog a request can pick from) ────────────────────
function SampleProductsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addName, setAddName] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  async function load() {
    setErr(null);
    try { setItems(await loadAllProductSamplesAdmin()); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function run(fn) {
    setBusy(true); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleAdd() {
    if (!addName.trim()) { setErr("Name is required."); return; }
    await run(async () => {
      const maxSort = items.reduce((m, i) => Math.max(m, i.sort_order ?? 0), 0);
      await createProductSample({ name: addName, description: addDesc, sortOrder: maxSort + 1 });
      setAddName(""); setAddDesc("");
    });
  }

  function startEdit(it) { setEditId(it.id); setEditName(it.name); setEditDesc(it.description ?? ""); }
  async function saveEdit(id) {
    if (!editName.trim()) { setErr("Name is required."); return; }
    await run(async () => {
      await updateProductSample(id, { name: editName, description: editDesc });
      setEditId(null);
    });
  }

  return (
    <>
      {err && <p className="error">{err}</p>}

      {/* Add sample */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", margin: "4px 0 18px" }}>
        <label style={{ flex: "1.4 1 200px", margin: 0 }}>Item Name
          <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="e.g. Banana Pudding 187ml 6pk"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
        </label>
        <label style={{ flex: "1.4 1 200px", margin: 0 }}>Description <span className="muted">(optional)</span>
          <input value={addDesc} onChange={(e) => setAddDesc(e.target.value)} placeholder="Short description"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
        </label>
        <button className="btn" type="button" disabled={busy || !addName.trim()} onClick={handleAdd} style={{ alignSelf: "flex-end" }}>Add Product Sample</button>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No product samples yet — add your first one above.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={cell}>Item Name</th>
                <th style={cell}>Description</th>
                <th style={{ ...cell, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const editing = editId === it.id;
                return (
                  <tr key={it.id}>
                    <td style={cell}>{editing ? <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus /> : <strong>{it.name}</strong>}</td>
                    <td style={cell}>{editing ? <input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description" /> : (it.description || <span className="muted">—</span>)}</td>
                    <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                      {editing ? (
                        <>
                          <button className="btn" type="button" disabled={busy} onClick={() => saveEdit(it.id)} style={{ marginRight: 8 }}>Save</button>
                          <button type="button" className="secondary" disabled={busy} onClick={() => setEditId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="link" disabled={busy} onClick={() => startEdit(it)} style={{ marginRight: 12 }}>Edit</button>
                          <button type="button" className="link" style={{ color: "var(--error)" }} disabled={busy} onClick={() => { if (window.confirm(`Delete "${it.name}"?`)) run(() => deleteProductSample(it.id)); }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
